import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import {
  MONTHS_RU,
  WEEKDAYS_RU,
  addDaysIso,
  addMonthsIso,
  formatIsoRu,
  isoDay,
  localDateTime,
  monthGrid,
  nowMs,
  parseRuDate,
  parseTime,
  shiftMonth,
  splitIso,
  timeSlots,
  type MonthRef,
} from '@dorixona/utils'
import { Icon, type IconName } from './Icon'

/** Месяц в дате: «13 октября 2026» — так дату читает читалка. */
const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]

const GAP = 4
/** Поле от края экрана, если панель не помещается ни вниз, ни вверх. */
const EDGE = 8

const todayIso = () => isoDay(nowMs())

function spokenDay(iso: string): string {
  const parts = splitIso(iso) as { year: number; month: number; day: number }
  return `${parts.day} ${MONTHS_GENITIVE[parts.month - 1]} ${parts.year}`
}

const inRange = (iso: string, min?: string, max?: string) =>
  (!min || iso >= min) && (!max || iso <= max)

// ── панель поверх страницы ──────────────────────────────────────────────────

interface Popover {
  open: boolean
  anchor: RefObject<HTMLDivElement>
  panel: RefObject<HTMLDivElement>
  show: () => void
  hide: () => void
}

/**
 * Где встать панели: вниз от поля, если помещается целиком, иначе вверх;
 * не помещается нигде — прижимаемся к краю экрана, чтобы низ с кнопками был виден.
 */
function placePanel(
  field: { top: number; bottom: number; left: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
): CSSProperties {
  const { height } = size
  const left = Math.max(EDGE, Math.min(field.left, viewport.width - size.width - EDGE))
  const below = viewport.height - field.bottom - GAP - EDGE
  const above = field.top - GAP - EDGE
  if (height <= below) return { left, top: field.bottom + GAP }
  if (height <= above) return { left, top: field.top - GAP - height }
  return { left, top: Math.max(EDGE, viewport.height - height - EDGE) }
}

/**
 * Панель прибита к полю: открывается вниз, а если места нет — вверх; клик
 * мимо, прокрутка страницы и смена размера окна её закрывают. Живёт в
 * портале — рамка прокрутки карточки её не обрежет.
 */
function usePopover(): Popover {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const inside = (target: EventTarget | null) =>
      target instanceof Node &&
      (anchor.current?.contains(target) === true || panel.current?.contains(target) === true)
    const onDown = (event: PointerEvent) => {
      if (!inside(event.target)) setOpen(false)
    }
    const onScroll = (event: Event) => {
      if (!inside(event.target)) setOpen(false)
    }
    const onResize = () => {
      setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  return {
    open,
    anchor,
    panel,
    show: () => {
      setOpen(true)
    },
    hide: () => {
      setOpen(false)
    },
  }
}

interface PanelProps {
  pop: Popover
  label: string
  /** Escape закрывает панель и возвращает фокус в поле. */
  onEscape: () => void
  children: ReactNode
}

function Panel({ pop, label, onEscape, children }: PanelProps) {
  const escape = useRef(onEscape)
  escape.current = onEscape
  // До отрисовки панель невидима: место считаем по её настоящей высоте.
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden', top: 0, left: 0 })
  useLayoutEffect(() => {
    const field = (pop.anchor.current as HTMLDivElement).getBoundingClientRect()
    const { width, height } = (pop.panel.current as HTMLDivElement).getBoundingClientRect()
    setStyle(
      placePanel(
        field,
        { width, height },
        {
          width: document.documentElement.clientWidth,
          height: window.innerHeight,
        },
      ),
    )
  }, [pop.anchor, pop.panel])
  // Escape внутри панели закрывает только её: окно, в котором стоит поле,
  // видит уже обработанное нажатие (defaultPrevented) и остаётся открытым.
  useEffect(() => {
    const node = pop.panel.current as HTMLDivElement
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      escape.current()
    }
    node.addEventListener('keydown', onKey)
    return () => {
      node.removeEventListener('keydown', onKey)
    }
  }, [pop.panel])

  return createPortal(
    <div ref={pop.panel} className="ui-dp-pop" role="dialog" aria-label={label} style={style}>
      {children}
    </div>,
    document.body,
  )
}

// ── календарь ───────────────────────────────────────────────────────────────

interface CalendarProps {
  value: string
  min?: string
  max?: string
  onPick: (iso: string) => void
}

/**
 * Месяц сеткой с понедельника. Стрелки ходят по дням, PageUp/PageDown — по
 * месяцам (с Shift — по годам), Home/End — к началу и концу недели. Подпись
 * месяца открывает выбор месяца и года: до дня рождения не листать годами.
 */
function Calendar({ value, min, max, onPick }: CalendarProps) {
  const today = todayIso()
  const start = value || today
  const [cursor, setCursor] = useState(start)
  const [month, setMonth] = useState<MonthRef>(() => {
    const parts = splitIso(start) as MonthRef
    return { year: parts.year, month: parts.month }
  })
  const [years, setYears] = useState(false)
  const grid = useRef<HTMLDivElement>(null)
  const moved = useRef(false)

  // Курсор ушёл стрелкой — фокус за ним; первое открытие тоже ставит фокус на день.
  useEffect(() => {
    if (years) return
    const button = grid.current?.querySelector<HTMLButtonElement>(`[data-iso="${cursor}"]`)
    if (button && (moved.current || !grid.current?.contains(document.activeElement))) {
      // Без прокрутки: панель прибита к полю, а прокрутка страницы её закрыла бы.
      button.focus({ preventScroll: true })
    }
    moved.current = false
  }, [cursor, month, years])

  function moveTo(iso: string) {
    const parts = splitIso(iso) as MonthRef
    moved.current = true
    setCursor(iso)
    setMonth({ year: parts.year, month: parts.month })
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const steps: Record<string, () => string> = {
      ArrowLeft: () => addDaysIso(cursor, -1),
      ArrowRight: () => addDaysIso(cursor, 1),
      ArrowUp: () => addDaysIso(cursor, -7),
      ArrowDown: () => addDaysIso(cursor, 7),
      PageUp: () => addMonthsIso(cursor, event.shiftKey ? -12 : -1),
      PageDown: () => addMonthsIso(cursor, event.shiftKey ? 12 : 1),
      Home: () => {
        const parts = splitIso(cursor) as { year: number; month: number; day: number }
        const weekday = monthGrid(parts).findIndex((c) => c.iso === cursor) % 7
        return addDaysIso(cursor, -weekday)
      },
      End: () => {
        const parts = splitIso(cursor) as { year: number; month: number; day: number }
        const weekday = monthGrid(parts).findIndex((c) => c.iso === cursor) % 7
        return addDaysIso(cursor, 6 - weekday)
      },
    }
    const step = steps[event.key]
    if (!step) return
    event.preventDefault()
    moveTo(step())
  }

  const cells = monthGrid(month)
  const title = `${MONTHS_RU[month.month - 1]} ${month.year}`
  const flip = (delta: number) => {
    setMonth(shiftMonth(month, delta))
  }

  if (years) {
    return (
      <div className="ui-cal">
        <div className="ui-cal-head">
          <button
            type="button"
            className="ui-iconbtn ui-cal-nav"
            aria-label="Предыдущий год"
            onClick={() => {
              flip(-12)
            }}
          >
            <Icon name="i-chevron-left" />
          </button>
          <label className="ui-cal-year">
            <span className="visually-hidden">Год</span>
            <input
              className="ui-input"
              type="number"
              inputMode="numeric"
              value={month.year}
              onChange={(e) => {
                const year = Number(e.target.value)
                if (year >= 1900 && year <= 2100) setMonth({ ...month, year })
              }}
            />
          </label>
          <button
            type="button"
            className="ui-iconbtn ui-cal-nav"
            aria-label="Следующий год"
            onClick={() => {
              flip(12)
            }}
          >
            <Icon name="i-chevron-right" />
          </button>
        </div>
        <div className="ui-cal-months" role="group" aria-label="Месяц">
          {MONTHS_RU.map((name, index) => (
            <button
              key={name}
              type="button"
              aria-pressed={month.month === index + 1}
              onClick={() => {
                setMonth({ ...month, month: index + 1 })
                setYears(false)
              }}
            >
              {name.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="ui-cal">
      <div className="ui-cal-head">
        <button
          type="button"
          className="ui-iconbtn ui-cal-nav"
          aria-label="Предыдущий месяц"
          onClick={() => {
            flip(-1)
          }}
        >
          <Icon name="i-chevron-left" />
        </button>
        <button
          type="button"
          className="ui-cal-title"
          aria-label={`${title}, выбрать месяц и год`}
          aria-live="polite"
          onClick={() => {
            setYears(true)
          }}
        >
          {title}
        </button>
        <button
          type="button"
          className="ui-iconbtn ui-cal-nav"
          aria-label="Следующий месяц"
          onClick={() => {
            flip(1)
          }}
        >
          <Icon name="i-chevron-right" />
        </button>
      </div>
      <div
        ref={grid}
        className="ui-cal-grid"
        role="grid"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="ui-cal-row" role="row">
          {WEEKDAYS_RU.map((day) => (
            <span key={day} role="columnheader" className="ui-cal-wd">
              {day}
            </span>
          ))}
        </div>
        {Array.from({ length: 6 }, (_, week) => (
          <div key={week} className="ui-cal-row" role="row">
            {cells.slice(week * 7, week * 7 + 7).map((cell) => (
              <span key={cell.iso} role="gridcell">
                <button
                  type="button"
                  className="ui-cal-day"
                  data-iso={cell.iso}
                  data-outside={cell.inMonth ? undefined : true}
                  aria-label={spokenDay(cell.iso)}
                  aria-pressed={cell.iso === value}
                  aria-current={cell.iso === today ? 'date' : undefined}
                  tabIndex={cell.iso === cursor ? 0 : -1}
                  disabled={!inRange(cell.iso, min, max)}
                  onClick={() => {
                    onPick(cell.iso)
                  }}
                >
                  {cell.day}
                </button>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── поле с кнопкой ──────────────────────────────────────────────────────────

interface FieldProps {
  pop: Popover
  id?: string
  label: string
  /** Поле без своего <label>: имя для читалки — `label`. */
  named: boolean
  icon: IconName
  text: string
  placeholder: string
  disabled: boolean
  invalid: boolean
  describedBy?: string
  input: RefObject<HTMLInputElement>
  onText: (text: string) => void
  onCommit: () => void
}

/** Поле, в которое можно напечатать, и кнопка, которая открывает панель. */
function Field({
  pop,
  id,
  label,
  named,
  icon,
  text,
  placeholder,
  disabled,
  invalid,
  describedBy,
  input,
  onText,
  onCommit,
}: FieldProps) {
  return (
    <div ref={pop.anchor} className="ui-dp" data-disabled={disabled || undefined}>
      <input
        ref={input}
        id={id}
        aria-label={named ? label : undefined}
        className="ui-input ui-dp-input"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        value={text}
        onChange={(e) => {
          onText(e.target.value)
        }}
        onBlur={onCommit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            onCommit()
          }
          if (event.key === 'ArrowDown' && event.altKey) {
            event.preventDefault()
            pop.show()
          }
        }}
      />
      <button
        type="button"
        className="ui-dp-btn"
        aria-label={`${label}: открыть выбор`}
        aria-haspopup="dialog"
        aria-expanded={pop.open}
        disabled={disabled}
        onClick={() => {
          if (pop.open) pop.hide()
          else pop.show()
        }}
      >
        <Icon name={icon} size={18} />
      </button>
    </div>
  )
}

/**
 * Текст поля следует за значением снаружи; пока человек печатает — его текст.
 * Значение сменилось снаружи (выбор в панели, «Отменить») — текст тоже.
 */
function useFieldText(value: string, format: (value: string) => string) {
  const [shown, setShown] = useState(value)
  const [text, setText] = useState(() => format(value))
  if (shown !== value) {
    setShown(value)
    setText(format(value))
  }
  return [text, setText] as const
}

// ── дата ────────────────────────────────────────────────────────────────────

export interface DatePickerProps {
  /** id поля ввода — для <label htmlFor>. */
  id?: string
  /** Название для читалки и заголовка панели. */
  label: string
  /** «2026-10-13»; пусто — дата не выбрана. */
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  disabled?: boolean
  invalid?: boolean
  describedBy?: string
  /** Можно оставить пустым: «Очистить» в панели и пустое поле. */
  clearable?: boolean
}

/**
 * Дата: печатают «13.10.2026» или выбирают в календаре. Неверная дата при
 * уходе из поля возвращается к прежней — значение формы не портится.
 */
export function DatePicker({
  id,
  label,
  value,
  onChange,
  min,
  max,
  disabled = false,
  invalid = false,
  describedBy,
  clearable = true,
}: DatePickerProps) {
  const pop = usePopover()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useFieldText(value, formatIsoRu)
  const today = todayIso()

  function commit() {
    const typed = text.trim()
    if (typed === '' && clearable) {
      if (value !== '') onChange('')
      return
    }
    const iso = parseRuDate(typed)
    if (iso && inRange(iso, min, max)) {
      setText(formatIsoRu(iso))
      if (iso !== value) onChange(iso)
      return
    }
    setText(formatIsoRu(value))
  }

  function pick(iso: string) {
    setText(formatIsoRu(iso))
    onChange(iso)
    pop.hide()
    input.current?.focus()
  }

  return (
    <>
      <Field
        pop={pop}
        id={id}
        label={label}
        named={id === undefined}
        icon="i-calendar"
        text={text}
        placeholder="дд.мм.гггг"
        disabled={disabled}
        invalid={invalid}
        describedBy={describedBy}
        input={input}
        onText={setText}
        onCommit={commit}
      />
      {pop.open ? (
        <Panel
          pop={pop}
          label={label}
          onEscape={() => {
            pop.hide()
            input.current?.focus()
          }}
        >
          <Calendar value={value} min={min} max={max} onPick={pick} />
          <div className="ui-dp-foot">
            <button
              type="button"
              className="ui-btn"
              data-variant="ghost"
              data-size="sm"
              disabled={!inRange(today, min, max)}
              onClick={() => {
                pick(today)
              }}
            >
              Сегодня
            </button>
            {clearable && value ? (
              <button
                type="button"
                className="ui-btn"
                data-variant="ghost"
                data-size="sm"
                onClick={() => {
                  setText('')
                  onChange('')
                  pop.hide()
                  input.current?.focus()
                }}
              >
                Очистить
              </button>
            ) : null}
          </div>
        </Panel>
      ) : null}
    </>
  )
}

// ── дата и время ────────────────────────────────────────────────────────────

/** Время по умолчанию, когда сначала выбрали день. */
const DEFAULT_TIME = '09:00'

const splitDateTime = (value: string) => {
  const [day = '', time = ''] = value.split('T')
  return { day, time }
}

function formatDateTime(value: string): string {
  const { day, time } = splitDateTime(value)
  const date = formatIsoRu(day)
  return date ? `${date}, ${time}` : ''
}

/** «13.10.2026, 11:16», «13.10.2026 9:30», «13.10.2026» (время — прежнее или 09:00). */
function parseDateTime(text: string, fallbackTime: string): string | null {
  const match = /^\s*(\S+?)[,\s]+(\S.*?)\s*$/.exec(text)
  const day = parseRuDate(match ? (match[1] as string) : text)
  const time = match ? parseTime(match[2] as string) : fallbackTime
  return day && time ? `${day}T${time}` : null
}

export interface DateTimePickerProps {
  id?: string
  label: string
  /** «2026-10-13T11:16»; пусто — не выбрано. */
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  invalid?: boolean
  describedBy?: string
}

/**
 * Дата и время в одной панели: календарь слева, время столбиком справа — как
 * в календарях почты. Время выбирают в списке (шаг 15 минут) или печатают
 * («9:30», «930»); «Сейчас» ставит текущий момент.
 */
export function DateTimePicker({
  id,
  label,
  value,
  onChange,
  disabled = false,
  invalid = false,
  describedBy,
}: DateTimePickerProps) {
  const pop = usePopover()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useFieldText(value, formatDateTime)
  const { day, time } = splitDateTime(value)
  const [timeText, setTimeText] = useFieldText(time, (t) => t)
  const timeId = useId()

  function commit() {
    const typed = text.trim()
    if (typed === '') {
      if (value !== '') onChange('')
      return
    }
    const next = parseDateTime(typed, time || DEFAULT_TIME)
    if (next) {
      setText(formatDateTime(next))
      if (next !== value) onChange(next)
      return
    }
    setText(formatDateTime(value))
  }

  const set = (nextDay: string, nextTime: string) => {
    onChange(`${nextDay}T${nextTime}`)
  }

  function commitTime() {
    const parsed = parseTime(timeText)
    if (parsed) {
      setTimeText(parsed)
      if (parsed !== time) set(day || todayIso(), parsed)
      return
    }
    setTimeText(time)
  }

  const close = () => {
    pop.hide()
    input.current?.focus()
  }

  return (
    <>
      <Field
        pop={pop}
        id={id}
        label={label}
        named={id === undefined}
        icon="i-calendar"
        text={text}
        placeholder="дд.мм.гггг, чч:мм"
        disabled={disabled}
        invalid={invalid}
        describedBy={describedBy}
        input={input}
        onText={setText}
        onCommit={commit}
      />
      {pop.open ? (
        <Panel pop={pop} label={label} onEscape={close}>
          <div className="ui-dp-split">
            <Calendar
              value={day}
              onPick={(iso) => {
                set(iso, time || DEFAULT_TIME)
              }}
            />
            <div className="ui-dp-time">
              <label className="ui-label" htmlFor={timeId}>
                Время
              </label>
              <input
                id={timeId}
                className="ui-input ui-dp-time-input"
                inputMode="numeric"
                autoComplete="off"
                placeholder="чч:мм"
                value={timeText}
                onChange={(e) => {
                  setTimeText(e.target.value)
                }}
                onBlur={commitTime}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    commitTime()
                  }
                }}
              />
              <TimeList
                label="Время"
                value={time}
                step={15}
                onPick={(slot) => {
                  set(day || todayIso(), slot)
                }}
              />
            </div>
          </div>
          <div className="ui-dp-foot">
            <button
              type="button"
              className="ui-btn"
              data-variant="ghost"
              data-size="sm"
              onClick={() => {
                onChange(localDateTime(nowMs()))
              }}
            >
              Сейчас
            </button>
            <button type="button" className="ui-btn" data-size="sm" onClick={close}>
              Готово
            </button>
          </div>
        </Panel>
      ) : null}
    </>
  )
}

// ── время ───────────────────────────────────────────────────────────────────

interface TimeListProps {
  label: string
  value: string
  step: number
  onPick: (time: string) => void
  /** Фокус сразу на выбранное время — когда список и есть вся панель. */
  focusSelected?: boolean
}

/**
 * Время столбиком с прокруткой. Открылся — уже прокручен к выбранному
 * времени, а если его нет — к утру: листать от полуночи не приходится.
 */
function TimeList({ label, value, step, onPick, focusSelected = false }: TimeListProps) {
  const list = useRef<HTMLUListElement>(null)
  const slots = timeSlots(step)
  const target = value || DEFAULT_TIME
  const nearest = slots.find((slot) => slot >= target) ?? slots[slots.length - 1]

  useLayoutEffect(() => {
    const box = list.current as HTMLUListElement
    const item = box.querySelector<HTMLElement>(`[data-time="${nearest}"]`) as HTMLElement
    // Сам список, а не страница: прокрутка страницы закрыла бы панель.
    box.scrollTop = item.offsetTop - box.clientHeight / 2 + item.offsetHeight / 2
    if (focusSelected) item.focus({ preventScroll: true })
  }, [nearest, focusSelected])

  return (
    <ul ref={list} className="ui-dp-times" aria-label={label}>
      {slots.map((slot) => (
        <li key={slot}>
          <button
            type="button"
            data-time={slot}
            aria-pressed={slot === value}
            onClick={() => {
              onPick(slot)
            }}
          >
            {slot}
          </button>
        </li>
      ))}
    </ul>
  )
}

export interface TimePickerProps {
  id?: string
  label: string
  /** «09:30»; пусто — не задано. */
  value: string
  onChange: (value: string) => void
  /** Шаг вариантов в списке, минуты. */
  step?: number
  disabled?: boolean
  invalid?: boolean
  describedBy?: string
}

/** Время: печатают («930», «9:30») или выбирают из списка с шагом. */
export function TimePicker({
  id,
  label,
  value,
  onChange,
  step = 30,
  disabled = false,
  invalid = false,
  describedBy,
}: TimePickerProps) {
  const pop = usePopover()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useFieldText(value, (v) => v)

  function commit() {
    const typed = text.trim()
    if (typed === '') {
      if (value !== '') onChange('')
      return
    }
    const parsed = parseTime(typed)
    if (parsed) {
      setText(parsed)
      if (parsed !== value) onChange(parsed)
      return
    }
    setText(value)
  }

  const close = () => {
    pop.hide()
    input.current?.focus()
  }

  return (
    <>
      <Field
        pop={pop}
        id={id}
        label={label}
        named={id === undefined}
        icon="i-clock"
        text={text}
        placeholder="чч:мм"
        disabled={disabled}
        invalid={invalid}
        describedBy={describedBy}
        input={input}
        onText={setText}
        onCommit={commit}
      />
      {pop.open ? (
        <Panel pop={pop} label={label} onEscape={close}>
          <TimeList
            label={label}
            value={value}
            step={step}
            focusSelected
            onPick={(slot) => {
              setText(slot)
              onChange(slot)
              close()
            }}
          />
        </Panel>
      ) : null}
    </>
  )
}
